// noinspection JSUnusedGlobalSymbols

/*
 * Redefine render so that we don't have to put _.template(templateName) for every template in a view
 * Extend Marionette views to include the i18n() function for translations
 * Create Marionette.Modal class which extends the Region class to support a modal view
 *
 * Extend Marionette.Controller to include subModule handling and custom close function
 */

define([
  "marionetteBase",
  "backbone",
  "underscore",
  "jquery",
  "messaging/messenger",
  "configuration/textbox",
  "loading/loadingView",
  "bootstrap",
  "i18n/i18n",
], function (MarionetteBase, Backbone, _, $, Messenger, Textbox, LoadingView) {
  "use strict";

  //tooltip options.  Delays showing the tooltip for 1/3 of a second.
  const tooltipDelay = { show: 333, hide: 0 };

  // Adjust for the fact that Marionette might have come from node_modules, in which case it includes a "default" value
  // at the top level that is really what we want
  // noinspection JSUnresolvedVariable
  const Marionette = MarionetteBase.default || MarionetteBase.Marionette || MarionetteBase;

  if (!Marionette.$) {
    Marionette.$ = $;
  }

  Marionette.Renderer.render = function (template, data) {
    if (!template) {
      console.error(new Error("No template given"));
      return "";
    }
    if (
      template.default &&
      (typeof template.default == "string" || typeof template.default == "function")
    ) {
      template = template.default;
    }
    if (typeof template == "string") {
      return _.template(template)(data);
    }
    if (typeof template == "function") {
      return template(data);
    }
    console.error("Unexpected template type, treating as string", template);
    return _.template(template.toString())(data);
  };

  Marionette.ItemView = Marionette.ItemView.extend({
    //enables the textbox at the given selector (needs to be defined in the textboxes hash)
    enableTextbox: function (selector) {
      const textboxes = _.result(this, "textboxes");
      if (textboxes) {
        const textbox = _.result(textboxes, selector);
        if (textbox) {
          const $selector = this.$el.find(selector);
          if ($selector.length && !Textbox.isCurrentTextBox($selector)) {
            let model;
            if (textbox.model) {
              model = this.model[textbox.model];
            } else {
              model = this.model;
            }
            Textbox.create(
              $selector,
              model,
              textbox.modelAttribute,
              textbox.textArea,
              textbox.placeholder,
              textbox.doNotModifyPlaceholder,
              textbox.isPassword,
            );
          }
        }
      }
    },
    focus: function () {
      if (this.$el) {
        this.$el.trigger("focus");
      }
    },
    onRender: function () {
      //internationalize the html
      this.$el.i18n();
      this._applyTooltips();
      this._applyTextboxes();
    },
    /*
     * Override serializeData with the addition of the _thisObject attribute to the data object.  The
     * _thisObject attribute is a reference to the Model or Collection in the view
     */
    serializeData: function () {
      let data = {};

      if (this.model) {
        data = this.model.toJSON();
        //extend data by including the model in data
        _.extend(data, { _thisObject: this.model });
      } else if (this.collection) {
        data = { items: this.collection.toJSON() };
        //extend data by including the collection in data
        _.extend(data, { _thisObject: this.collection });
      }

      return data;
    },
    _applyTooltips: function () {
      this.$el
        .find(
          'a[data-toggle="tooltip"], button[data-toggle="tooltip"], input[data-toggle="tooltip"]',
        )
        .tooltip({ delay: tooltipDelay, container: "body" });
    },
    _applyTextboxes: function () {
      //for each textbox, set up the proper classes and insert either the placeholder text or the model's attribute value
      const textboxes = _.result(this, "textboxes");
      if (textboxes) {
        _.each(
          textboxes,
          function (textbox, selector) {
            const $selector = this.$el.find(selector);
            //insert text or placeholder into the textbox area
            let model;
            if (textbox.model) {
              model = this.model[textbox.model];
            } else {
              model = this.model;
            }
            Textbox.setInitialText(
              $selector,
              model,
              textbox.modelAttribute,
              textbox.textArea,
              textbox.placeholder,
              textbox.doNotModifyPlaceholder,
              textbox.isPassword,
            );
          },
          this,
        );
      }
    },
    //creates textbox events from the textbox hash.  Basically creates a "click selector" event with the
    //callback being a function to create a textbox
    _configureTextboxEvents: function () {
      const textboxEvents = {};
      const textboxes = _.result(this, "textboxes");
      if (textboxes) {
        _.each(
          textboxes,
          function (textbox, selector) {
            const callbackFunction = this.enableTextbox.bind(this, selector);
            //if enableSelector is an array, create an event for each selector in the array
            if (_.isArray(textbox.enableSelector)) {
              for (let i = 0; i < textbox.enableSelector.length; i++) {
                textboxEvents["click " + textbox.enableSelector[i]] = callbackFunction;
              }
            } else {
              //just one selector, so create one event for it
              textboxEvents["click " + textbox.enableSelector] = callbackFunction;
            }
          },
          this,
        );
      }
      return textboxEvents;
    },
    // internal method to delegate DOM events and triggers
    //exteneded to include any events for textboxes
    _delegateDOMEvents: function (events) {
      events = events || this.events;
      if (_.isFunction(events)) {
        events = events.call(this);
      }

      const combinedEvents = {};
      const triggers = this.configureTriggers();
      //create events from textboxes (if they exist)
      const textboxEvents = this._configureTextboxEvents();

      _.extend(combinedEvents, events, triggers, textboxEvents);

      Backbone.View.prototype.delegateEvents.call(this, combinedEvents);
    },
  });

  Marionette.CollectionView = Marionette.CollectionView.extend({
    focus: function () {
      if (this.$el) {
        this.$el.trigger("focus");
      }
    },
    onRender: function () {
      this.$el.i18n();
      this.$el.find('a[data-toggle="tooltip"], button[data-toggle="tooltip"]').tooltip({
        delay: tooltipDelay,
        container: "body",
      });
      //if tooltips were already assigned in the collection's views, then this will leave behind stray title properties.  Get rid of them.
      this.$el.find("a[title], button[title]").prop("title", "");
    },
    serializeData: function () {
      let data = {};

      if (this.model) {
        data = this.model.toJSON();
      }
      //extend data by including the model in data
      _.extend(data, { _thisObject: this.model || this.collection });

      return data;
    },
  });

  Marionette.CompositeView = Marionette.CompositeView.extend({
    onRender: function () {
      this.$el.i18n();
      this.$el.find('a[data-toggle="tooltip"], button[data-toggle="tooltip"]').tooltip({
        delay: tooltipDelay,
        container: "body",
      });
      //if tooltips were already assigned in the collection's views, then this will leave behind stray title properties.  Get rid of them.
      this.$el.find("a[title], button[title]").prop("title", "");
    },
    focus: function () {
      if (this.$el) {
        this.$el.trigger("focus");
      }
    },
    serializeData: function () {
      let data;

      if (this.model) {
        data = this.model.toJSON();
        //extend data by including the model in data
        _.extend(data, { _thisObject: this.model });
      } else if (this.collection) {
        data = this.collection.toJSON();
        //extend data by including the model in data
        _.extend(data, { _thisObject: this.collection });
      } else {
        data = {};
      }

      return data;
    },
  });

  Marionette.Layout = Marionette.Layout.extend({
    //enables the textbox at the given selector (needs to be defined in the textboxes hash)
    enableTextbox: function (selector) {
      const textboxes = _.result(this, "textboxes");
      if (textboxes) {
        const textbox = textboxes[selector];
        if (textbox) {
          const $selector = this.$el.find(selector);
          if (!Textbox.isCurrentTextBox($selector)) {
            let model;
            if (textbox.model) {
              model = this.model[textbox.model];
            } else {
              model = this.model;
            }
            Textbox.create(
              $selector,
              model,
              textbox.modelAttribute,
              textbox.textArea,
              textbox.placeholder,
              textbox.doNotModifyPlaceholder,
              textbox.isPassword,
            );
          }
        }
      }
    },
    focus: function () {
      if (this.$el) {
        this.$el.trigger("focus");
      }
    },
    onRender: function () {
      this.$el.i18n();
      this.$el.find('a[data-toggle="tooltip"], button[data-toggle="tooltip"]').tooltip({
        delay: tooltipDelay,
        container: "body",
      });
      //apply textboxes defined in this.textboxes
      this._applyTextboxes();
    },
    /*
     * Copy of the serializeData function from ItemView, since Layout is an extension of ItemView
     */
    serializeData: function () {
      let data = {};

      if (this.model) {
        data = this.model.toJSON();
        //extend data by including the model in data
        _.extend(data, { _thisObject: this.model });
      } else if (this.collection) {
        data = { items: this.collection.toJSON() };
        //extend data by including the collection in data
        _.extend(data, { _thisObject: this.collection });
      }

      return data;
    },
    _applyTextboxes: function () {
      //for each textbox, set up the proper classes and insert either the placeholder text or the model's attribute value
      const textboxes = _.result(this, "textboxes");
      if (textboxes) {
        _.each(
          textboxes,
          function (textbox, selector) {
            const $selector = this.$el.find(selector);
            //insert text or placeholder into the textbox area
            let model;
            if (textbox.model) {
              model = this.model[textbox.model];
            } else {
              model = this.model;
            }
            Textbox.setInitialText(
              $selector,
              model,
              textbox.modelAttribute,
              textbox.textArea,
              textbox.placeholder,
              textbox.doNotModifyPlaceholder,
            );
          },
          this,
        );
      }
    },
    //creates textbox events from the textbox hash.  Basically creates a "click selector" event with the
    //callback being a function to create a textbox
    _configureTextboxEvents: function () {
      const textboxEvents = {};
      const textboxes = _.result(this, "textboxes");
      if (textboxes) {
        _.each(
          textboxes,
          function (textbox, selector) {
            const callbackFunction = function () {
              //create textbox
              const $selector = this.$el.find(selector);
              //selector must exist and must not be the current textbox
              if ($selector.length && !Textbox.isCurrentTextBox($selector)) {
                let model;
                if (textbox.model) {
                  model = this.model[textbox.model];
                } else {
                  model = this.model;
                }
                Textbox.create(
                  $selector,
                  model,
                  textbox.modelAttribute,
                  textbox.textArea,
                  textbox.placeholder,
                  textbox.doNotModifyPlaceholder,
                  false,
                  textbox.typeahead,
                  textbox.doNotAllowEmpty,
                  textbox.emptyTextboxMessage,
                );
              }
            };
            //if enableSelector is an array, create an event for each selector in the array
            if (_.isArray(textbox.enableSelector)) {
              for (let i = 0; i < textbox.enableSelector.length; i++) {
                textboxEvents["click " + textbox.enableSelector[i]] = callbackFunction;
              }
            } else {
              //just one selector, so create one event for it
              textboxEvents["click " + textbox.enableSelector] = callbackFunction;
            }
          },
          this,
        );
      }
      return textboxEvents;
    },
    // internal method to delegate DOM events and triggers
    //exteneded to include any events for textboxes
    _delegateDOMEvents: function (events) {
      events = events || this.events;
      if (_.isFunction(events)) {
        events = events.call(this);
      }

      const combinedEvents = {};
      const triggers = this.configureTriggers();
      //create events from textboxes (if they exist)
      const textboxEvents = this._configureTextboxEvents();

      _.extend(combinedEvents, events, triggers, textboxEvents);

      Backbone.View.prototype.delegateEvents.call(this, combinedEvents);
    },
  });

  Marionette.Modal = Marionette.Region.extend({
    onClose: function () {
      //when we close a modal view, make sure that we clear out the modal data so that it can be properly re-initialized
      //also remove the backdrop so it doesn't get drawn over
      Messenger.vent.trigger("destroy:modal");
      this.$el.modal("removeBackdrop");
      this.$el.data("modal", null);
      //finally, stop listening for the 'hidden' event in case the view was closed without triggering the 'hidden' event.
      //this happens when a modal opens another modal (such as the learner transcript opening up an example editor)
      this.$el.off("hidden");
      //trigger an event that the modal is closed
      Messenger.vent.trigger("modal:closed");
    },
    onShow: function () {
      const _this = this;
      //create the bootstrap modal
      this.$el.modal({
        backdrop: this.backdrop,
        keyboard: false, //do not close modal using ESC key
      });
      //close the modal's view when the modal is hidden
      this.$el.one("hidden", function () {
        _this.close();
      });
    },
  });

  Marionette.Controller = Marionette.Controller.extend({
    isOpen: false,
    close: function () {
      // This is not always called from a Controller, so make sure the functions exist before calling them
      if (typeof this.clearTimeouts === "function") {
        this.clearTimeouts();
      }
      if (typeof this.closeReqRes === "function") {
        this.closeReqRes();
      }
      if (typeof this.closeVents === "function") {
        this.closeVents();
      }
      if (typeof this.closeSubModules === "function") {
        this.closeSubModules();
      }
      // Copied from base close() method
      this.stopListening();
      this.triggerMethod("close");
      this.unbind();
    },
    //handles showing the view in the region
    showRegion: function (view, args) {
      //use the closing of the view to close the controller
      this.listenTo(
        view,
        "close",
        function () {
          this.isOpen = false; //module is not open anymore
          this.closeSubModules();
        },
        this,
      );

      args = args || {};
      const region = args.region || this.region;
      const loading = args.loading || false;
      const debug = args.debug || false;
      const entity = args.entity || view.collection || view.model;
      let entities = args.entities;
      if (!entities && entity) {
        entities = [entity];
      }
      //loading must be enabled, we must have a set of models/collections to follow, and each model/collection
      //must be requesting data from the server and has not fetched it successfully yet
      if (loading && entities && entities.length) {
        let loadingCount = 0;
        let loadingView = null;
        entities.forEach(function (entity) {
          if (entity && _.isFunction(entity.checkFetching) && entity.checkFetching()) {
            ++loadingCount;
            if (!loadingView) {
              loadingView = new LoadingView.loadingView();
              // also listen for the loadingView to close, in case the module gets closed while loading is
              // still occurring (we need to stop listening before we replace the loading view with the
              // real view, however).
              this.listenTo(
                loadingView,
                "close",
                function () {
                  this.isOpen = false; //module is not open anymore
                  this.closeSubModules();
                },
                this,
              );
            }
            if (!debug) {
              entity.once(
                "fetched",
                function () {
                  --loadingCount;
                  if (loadingCount === 0) {
                    if (region.currentView === loadingView) {
                      this.stopListening(loadingView); //stop listening to the loading view because it will be closed in the line below
                      region.show(view);
                      this.isOpen = true;
                    } else {
                      view.close();
                    }
                  }
                },
                this,
              );
            }
          }
        }, this);
        if (loadingCount) {
          region.show(loadingView);
          return;
        }
      }
      region.show(view);
      this.isOpen = true;
    },
    //adds sub modules to the controller
    addSubModule: function (module) {
      if (typeof this.subModules === "undefined") {
        this.subModules = [];
      }
      this.subModules.push(module);
      return module; //return the passed module to simplify assignment
    },
    //overwrites the close function, closes a module's sub-modules, but doesn't close itself
    closeSubModules: function () {
      //console.log("close " + this.name + " module");
      if (typeof this.subModules !== "undefined" && this.subModules.length > 0) {
        /*
         * If you call show() on a module multiple times, then the original submodules are not removed
         * until AFTER the new ones are added.  This is because the view doesn't close until the next
         * view is attempted to be shown.  As a result, we need to check when removing sub modules
         * whether there are both old and new sub modules still in existence.  We check that by looking for a duplicate el.
         * If we find two modules with a duplicate el, that means we have half old modules and half new modules in the array.
         * We can simply check if the array is of even length, and whether the module at index 0 has the same el
         * as the module at index length/2.  If so, then we slice out the first length/2 modules.
         */
        const arrLength = this.subModules.length;
        let i;
        if (
          arrLength % 2 === 0 &&
          this.subModules[0].region.el === this.subModules[arrLength / 2].region.el
        ) {
          console.log("Deleting old sub modules");
          for (i = 0; i < arrLength / 2; i++) {
            this._closeSubModule(this.subModules[i]);
          }
          //slice sub module array to only include second half
          this.subModules = this.subModules.slice(arrLength / 2);
        } else {
          //normally clear out sub modules
          for (i = 0; i < arrLength; i++) {
            this._closeSubModule(this.subModules[i]);
          }
          this.subModules = []; //reset submodules array
        }
      }
    },
    closeVents: function () {
      //shut down vents
      Messenger.vent.off(null, null, this);
    },
    clearTimeouts: function () {
      if (this.timeouts && this.timeouts.length) {
        this.timeouts.forEach(function (id) {
          clearTimeout(id);
        });
        delete this.timeouts;
      }
    },
    setTimeout: function (callback, timeout) {
      if (typeof this.timeouts === "undefined") {
        this.timeouts = [];
      }
      const interval = setTimeout(callback, timeout);
      this.timeouts.push(interval);
      return interval;
    },
    closeReqRes: function () {
      //shut down requests
      Messenger.reqres.removeHandlersByContext(this);
    },
    _closeSubModule: function (subModule) {
      //close submodule vents (don't close top level module vents...we need those
      console.log("close submodule: " + subModule.name + " (parent: " + this.name + ")");
      subModule.clearTimeouts();
      subModule.closeVents();
      subModule.closeReqRes();
      subModule.close();
      //release object (probably excessive)
      subModule = null;
    },
  });

  return Marionette;
});
